import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  Card,
  CardContent
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users,
  BookOpen,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Wallet
} from 'lucide-react';

function StatCard({
  title,
  value,
  icon: Icon,
  color,
  testId,
  subtitle
}) {
  return (
    <Card
      className="bg-[#141414] border-[#27272A]"
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[10px] tracking-[0.15em] uppercase font-bold text-zinc-500">
              {title}
            </p>

            <p className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight text-white mt-1">
              {value}
            </p>

            {subtitle && (
              <p className="text-xs text-zinc-500 mt-1">
                {subtitle}
              </p>
            )}
          </div>

          <div
            className={`w-10 h-10 rounded-lg flex items-center justify-center ${color}`}
          >
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function dinheiro(valor) {
  return `R$ ${Number(valor || 0).toLocaleString(
    'pt-BR',
    {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }
  )}`;
}

export default function GestorDashboard() {
  const { user } = useAuth();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get('/gestor/dashboard')
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton
              key={i}
              className="h-24 bg-[#141414]"
            />
          ))}
        </div>
      </div>
    );
  }

  if (
    user?.role === 'socio'
    || stats?.tipo_dashboard === 'socio'
  ) {
    return (
      <div
        className="space-y-6 animate-fadeIn"
        data-testid="socio-dashboard"
      >
        <div>
          <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
            DASHBOARD
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Participação societária:{' '}
            {Number(
              stats?.percentual_sociedade || 0
            ).toLocaleString('pt-BR', {
              maximumFractionDigits: 2
            })}
            %
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Previsão do mês"
            value={dinheiro(stats?.previsao_mes)}
            icon={TrendingUp}
            color="bg-[#007AFF]/10 text-[#007AFF]"
            subtitle={`Competência ${stats?.competencia || '-'}`}
            testId="socio-previsao-mes"
          />

          <StatCard
            title="Valor recebido"
            value={dinheiro(stats?.recebido_mes)}
            icon={DollarSign}
            color="bg-green-500/10 text-green-400"
            subtitle={`Custos: ${dinheiro(stats?.custos_mes)}`}
            testId="socio-recebido-mes"
          />

          <StatCard
            title="Previsão de ganho do sócio"
            value={dinheiro(
              stats?.previsao_ganho_socio
            )}
            icon={Wallet}
            color="bg-purple-500/10 text-purple-400"
            subtitle="Percentual aplicado após os custos"
            testId="socio-previsao-ganho"
          />

          <StatCard
            title="Valor a receber"
            value={dinheiro(
              stats?.valor_a_receber_socio
            )}
            icon={DollarSign}
            color="bg-amber-500/10 text-amber-400"
            subtitle="Baseado no que já foi recebido"
            testId="socio-valor-receber"
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="space-y-6 animate-fadeIn"
      data-testid="gestor-dashboard"
    >
      <h1 className="text-3xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
        DASHBOARD
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Alunos Ativos"
          value={stats?.total_alunos || 0}
          icon={Users}
          color="bg-[#007AFF]/10 text-[#007AFF]"
          testId="gestor-stats-alunos"
        />

        <StatCard
          title="Turmas"
          value={stats?.total_turmas || 0}
          icon={BookOpen}
          color="bg-purple-500/10 text-purple-400"
          testId="gestor-stats-turmas"
        />

        <StatCard
          title="Inadimplentes"
          value={stats?.inadimplentes || 0}
          icon={AlertTriangle}
          color="bg-red-500/10 text-red-400"
          testId="gestor-stats-inadimplentes"
        />

        <StatCard
          title="Faturamento"
          value={dinheiro(stats?.faturamento)}
          icon={DollarSign}
          color="bg-amber-500/10 text-amber-400"
          testId="gestor-stats-faturamento"
        />
      </div>
    </div>
  );
}
